// apps/smart-shikka-web/src/pages/Skills.tsx
// REF-03: Skills page — canonical /api/skills/registry, read-only, search filtered.
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../auth.js";
import { AppNav } from "../components/AppNav.js";
import { useBrandColors } from "@smart-shikka/react";

const BASE = () => ((import.meta as any).env?.VITE_API_BASE_URL as string | undefined) ?? window.location.origin;

interface Skill {
  id: string;
  displayName: string;
  description?: string;
  category?: string;
  executionMode?: string;
  riskLevel?: string;
}

function Chip({ mode }: { mode: string }) {
  const C = useBrandColors();
  const enabled = mode !== "disabled";
  const label = mode === "metadata_preview" ? "metadata" : mode === "dry_run" ? "dry-run" : mode === "disabled" ? "अक्षम" : mode;
  return (
    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background: enabled ? C.successBg : C.warningBg, color: enabled ? C.successFg : C.warning, fontWeight:600 }}>
      {label}
    </span>
  );
}

function riskColor(level: string | undefined, C: ReturnType<typeof useBrandColors>): string {
  if (level === "critical" || level === "high") return C.destructive;
  if (level === "medium") return C.warning;
  return C.muted;
}

export function SkillsPage() {
  const { token } = useAuth();
  const C = useBrandColors();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const hdrs = useCallback((): HeadersInit => ({ "Content-Type":"application/json", ...(token ? { Authorization:`Bearer ${token}` } : {}) }), [token]);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    fetch(`${BASE()}/api/skills/registry`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const arr = d?.data ?? d?.skills ?? (Array.isArray(d) ? d : []);
        setSkills((arr as Record<string,unknown>[]).map((s) => {
          const skill: Skill = {
            id: String(s.skillId ?? s.id ?? s.name),
            displayName: String(s.displayName ?? s.name ?? s.skillId ?? s.id),
          };
          if (typeof s.description === "string") skill.description = s.description;
          if (typeof s.category === "string") skill.category = s.category;
          if (typeof s.executionMode === "string") skill.executionMode = s.executionMode;
          if (typeof s.riskLevel === "string") skill.riskLevel = s.riskLevel;
          return skill;
        }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, hdrs]);

  const filtered = search
    ? skills.filter(s =>
        s.displayName.toLowerCase().includes(search.toLowerCase()) ||
        (s.description ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (s.category ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : skills;

  return (
    <div style={{ display:"flex", height:"100%", background:C.bg, fontFamily:C.fontSans }}>
      <AppNav activeRoute="skills" />

      {/* Sub-nav */}
      <aside style={{ width:200, background:C.panel, borderRight:`1px solid ${C.border}`, display:"flex", flexDirection:"column", padding:"12px 8px", gap:2, flexShrink:0 }}>
        <div style={{ marginBottom:6, padding:"0 4px" }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="खोजें" style={{ width:"100%", padding:"7px 10px", borderRadius:6, border:`1px solid ${C.border}`, background:C.bg, fontSize:11, color:C.fg, outline:"none", boxSizing:"border-box" as const, fontFamily:"inherit" }} />
        </div>
        {["सेटिंग्स","क्षमताएं"].map((l, i) => {
          const active = i === 1;
          return (
            <a key={l} href="#settings" onClick={(e:React.MouseEvent) => { e.preventDefault(); window.location.hash="#settings"; }} style={{ display:"block", padding:"7px 10px", borderRadius:6, textDecoration:"none", color:active ? C.accentFg : C.fg, background:active ? C.accent : "transparent", fontSize:12, cursor:"pointer" }}>{l}</a>
          );
        })}
      </aside>

      {/* Content */}
      <main style={{ flex:1, overflow:"auto", padding:"28px 36px" }}>
        <div style={{ maxWidth:760 }}>
          <div style={{ fontSize:10, color:C.muted, marginBottom:4 }}>✦ आपका कार्यक्षेत्र</div>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap" as const, gap:10 }}>
            <div>
              <h1 style={{ fontSize:22, fontWeight:700, color:C.fg, margin:"0 0 4px" }}>स्किल्स</h1>
              <p style={{ fontSize:12, color:C.muted, margin:0 }}>अपने काम के लिए सही हुनर चुनें।</p>
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <div style={{ fontSize:11, color:C.muted }}>{filtered.length} / {skills.length} स्किल्स</div>
            </div>
          </div>

          <div style={{ border:`1px solid ${C.border}`, borderRadius:8, overflow:"hidden", background:C.card }}>
            <table style={{ width:"100%", borderCollapse:"collapse" as const }}>
              <thead>
                <tr style={{ borderBottom:`1px solid ${C.border}`, background:C.panel }}>
                  {["कौशल","विवरण","श्रेणी","स्थिति"].map(h => (
                    <th key={h} style={{ padding:"10px 14px", textAlign:"left" as const, fontSize:11, fontWeight:600, color:C.muted }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={4} style={{ padding:"24px 14px", textAlign:"center" as const, fontSize:12, color:C.muted }}>लोड हो रहे हैं...</td></tr>}
                {!loading && filtered.length===0 && <tr><td colSpan={4} style={{ padding:"24px 14px", textAlign:"center" as const, fontSize:12, color:C.muted }}>कोई स्किल नहीं।</td></tr>}
                {filtered.map((s, i) => (
                  <tr key={s.id} style={{ borderBottom:i<filtered.length-1 ? `1px solid ${C.border}`:"none" }}
                    onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background=C.accent; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background="transparent"; }}>
                    <td style={{ padding:"12px 14px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <div style={{ width:20, height:20, borderRadius:"50%", background:C.accent, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, color:C.accentFg, flexShrink:0 }}>✦</div>
                        <span style={{ fontSize:12, color:C.fg, fontWeight:500, fontFamily:C.fontSans }}>{s.displayName}</span>
                      </div>
                    </td>
                    <td style={{ padding:"12px 14px", fontSize:12, color:C.muted, maxWidth:260, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.description ?? "—"}</td>
                    <td style={{ padding:"12px 14px", fontSize:12, color:C.muted }}>{s.category ?? "—"}</td>
                    <td style={{ padding:"12px 14px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <Chip mode={s.executionMode ?? "disabled"} />
                        <span style={{ fontSize:10, color:riskColor(s.riskLevel, C) }}>{s.riskLevel ?? ""}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop:16, padding:"14px 16px", border:`1px solid ${C.border}`, borderRadius:8, background:C.card, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:C.fg, marginBottom:3 }}>यह रजिस्ट्री केवल पढ़ने योग्य है</div>
              <div style={{ fontSize:11, color:C.muted }}>स्किल चालू/बंद करने के लिए Control Center या CEO turn का उपयोग करें।</div>
            </div>
            <div style={{ width:32, height:32, borderRadius:"50%", background:C.accent, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, color:C.accentFg }}>✦</div>
          </div>
        </div>
      </main>
    </div>
  );
}
